import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, ROLES } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Lock } from "lucide-react";

const formatErr = (d) => {
  if (!d) return "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return d.msg || JSON.stringify(d);
};

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [authMethod, setAuthMethod] = useState("google"); // google | password
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "seller" });

  React.useEffect(() => {
    if (!loading && user) {
      if (!user.role_selected) navigate("/select-role", { replace: true });
      else navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const startGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitPassword = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register"
        ? { email: form.email, password: form.password, name: form.name || null, role: form.role }
        : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      setUser(data.user);
      toast.success(mode === "register" ? "Account created" : "Welcome back");
      if (!data.user.role_selected) navigate("/select-role", { replace: true });
      else navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Auth failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center bg-[var(--adn-cream)] py-16 px-4">
        <div className="w-full max-w-md bg-white border border-[var(--adn-line)] shadow-sm">
          {/* Header */}
          <div className="px-8 pt-8 pb-4">
            <p className="eyebrow">{mode === "register" ? "Create your account" : "Sign in"}</p>
            <h1 className="font-display text-3xl font-semibold mt-2">
              {mode === "register" ? "Join ADN Marketplace" : "Welcome back"}
            </h1>
            <p className="text-[var(--adn-text-soft)] mt-2 text-sm leading-relaxed">
              {mode === "register"
                ? "One account works for sellers and providers."
                : "Sign in with Google or with your email & password."}
            </p>
          </div>

          {/* Method tabs */}
          <div className="px-8">
            <div className="flex border-b border-[var(--adn-line)]">
              <button
                data-testid="tab-google"
                onClick={() => setAuthMethod("google")}
                className={`flex-1 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  authMethod === "google"
                    ? "border-[var(--adn-gold)] text-[var(--adn-navy)]"
                    : "border-transparent text-[var(--adn-text-muted)] hover:text-[var(--adn-text)]"
                }`}
              >
                Google
              </button>
              <button
                data-testid="tab-password"
                onClick={() => setAuthMethod("password")}
                className={`flex-1 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  authMethod === "password"
                    ? "border-[var(--adn-gold)] text-[var(--adn-navy)]"
                    : "border-transparent text-[var(--adn-text-muted)] hover:text-[var(--adn-text)]"
                }`}
              >
                Email & Password
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-6">
            {authMethod === "google" ? (
              <>
                <button
                  data-testid="login-google"
                  onClick={startGoogleAuth}
                  className="w-full flex items-center justify-center gap-3 bg-white border border-[var(--adn-line)] hover:border-[var(--adn-navy)] transition-colors px-5 py-3.5 text-[15px] font-medium"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                    <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
                    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.95 11.95 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                  </svg>
                  Continue with Google
                </button>
                <p className="text-[11px] text-[var(--adn-text-muted)] mt-5 leading-relaxed text-center">
                  Uses your Google password. We never see or store it.
                </p>
              </>
            ) : (
              <form onSubmit={submitPassword} className="space-y-3">
                {mode === "register" && (
                  <>
                    <Field icon={null} label="Full name (optional)">
                      <input
                        data-testid="register-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="auth-input"
                        placeholder="Jane Smith"
                      />
                    </Field>
                    <Field label="I am a…">
                      <select
                        data-testid="register-role"
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="auth-input"
                      >
                        <option value="seller">Property Seller / Homeowner</option>
                        {ROLES.filter((r) => r.id !== "seller").map((r) => (
                          <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                      </select>
                    </Field>
                  </>
                )}
                <Field icon={Mail} label="Email">
                  <input
                    data-testid={mode === "register" ? "register-email" : "login-email"}
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="auth-input pl-9"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Field>
                <Field icon={Lock} label="Password">
                  <input
                    data-testid={mode === "register" ? "register-password" : "login-password"}
                    required
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="auth-input pl-9"
                    placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    minLength={mode === "register" ? 8 : undefined}
                  />
                </Field>

                <button
                  data-testid={mode === "register" ? "register-submit" : "login-submit"}
                  disabled={busy}
                  type="submit"
                  className="btn-gold w-full px-5 py-3 rounded text-sm font-semibold inline-flex items-center justify-center gap-2"
                >
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {mode === "register" ? "Create Account" : "Sign In"}
                </button>

                <div className="pt-3 text-center text-sm text-[var(--adn-text-soft)]">
                  {mode === "register" ? (
                    <>Already have an account?{" "}
                      <button type="button" data-testid="switch-to-login" onClick={() => setMode("login")} className="text-[var(--adn-navy)] underline hover:text-[var(--adn-gold)]">
                        Sign in
                      </button>
                    </>
                  ) : (
                    <>No account yet?{" "}
                      <button type="button" data-testid="switch-to-register" onClick={() => setMode("register")} className="text-[var(--adn-navy)] underline hover:text-[var(--adn-gold)]">
                        Create one
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}

            <p className="text-[11px] text-[var(--adn-text-muted)] mt-6 leading-relaxed">
              By continuing you agree to ADN's Terms and Privacy Policy. Sellers always submit free.
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <style>{`
        .auth-input { width: 100%; background: white; border: 1px solid #CBD5E1; padding: 10px 12px; font-size: 14px; color: var(--adn-text); }
        .auth-input:focus { outline: none; border-color: var(--adn-navy); box-shadow: 0 0 0 3px rgba(11,19,43,0.08); }
      `}</style>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">{label}</span>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--adn-text-muted)] pointer-events-none" />}
        {children}
      </div>
    </label>
  );
}
